import type { WhereSelection } from "@ui/components/SearchBar";
import { useCallback, useEffect, useRef, useState } from "react";
import { type WhereFilter, whereFilterKey } from "@/lib/where-regions";
import type { MapBounds } from "@/types/search-map";

const BOUNDS_DEBOUNCE_MS = 250;

type BoundsSource = "where" | "map" | null;

// A zoomed-out map reports bounds past the poles/antimeridian (e.g. lng ±183).
// Elasticsearch wraps those; OpenSearch rejects the geo_bounding_box outright
// ("longitude is invalid"), failing the whole search. Clamp to the valid ranges
// so a world-spanning view degrades to the full globe instead of erroring.
const clampLat = (value: number) => Math.max(-90, Math.min(90, value));
const clampLng = (value: number) => Math.max(-180, Math.min(180, value));

export interface UseSearchBoundsSyncOptions {
  /** Searchkit-formatted bbox string derived from a Where selection. */
  whereBoundsBox: string | null;
  /** Current Where pill value — clearing it wipes any active bounds filter. */
  whereValue: WhereSelection | null;
  /** Term narrowing for the current Where selection, on indexes that filter by term. */
  whereFilter?: WhereFilter | null;
  /** Mark the Where pill as a synthetic "map-area" suggestion after a user pan. */
  markMapArea: (label: string) => void;
  /** Translated label for the map-area pill (component-i18n owns the key). */
  mapAreaLabel: string;
}

export interface UseSearchBoundsSyncReturn {
  /** Feed to `<Configure insideBoundingBox={boundsBox} />` when present. */
  boundsBox: string | null;
  /** Lets the consumer decide between auto-fitting hits, fitting a bbox, or pinning the map. */
  boundsSource: BoundsSource;
  /** Wire to the map's `onUserBoundsChange` prop. Debounces and flips source to "map". */
  handleBoundsChange: (bounds: MapBounds) => void;
}

/**
 * Mirrors a Where-derived bbox into Searchkit's `insideBoundingBox` filter and
 * lets user-driven map pans take over the filter afterwards. Two consumers
 * (programs, destinations) share the exact state machine — extracting it keeps
 * them in lockstep.
 */
export function useSearchBoundsSync({
  whereBoundsBox,
  whereValue,
  whereFilter = null,
  markMapArea,
  mapAreaLabel,
}: UseSearchBoundsSyncOptions): UseSearchBoundsSyncReturn {
  const [boundsBox, setBoundsBox] = useState<string | null>(null);
  const [boundsSource, setBoundsSource] = useState<BoundsSource>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const whereFilterId = whereFilterKey(whereFilter);

  // Mirror the where-derived bbox into Searchkit's filter and let the map auto-fit.
  // A subsequent map pan flips the source to "map" and pins the view.
  //
  // A term selection carries no bounds box but is still a Where selection for framing
  // (the map host fits `whereBbox` whenever boundsSource is "where"), so it runs through
  // here too — writing a null box, which is what clears the stale map bbox a prior pan
  // left behind. Leaving that geo filter in place would intersect with the term.
  // `whereFilterId` stands in for the filter itself: a stable key over an object rebuilt each render.
  useEffect(() => {
    if (whereBoundsBox === null && whereFilterId === null) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setBoundsBox(whereBoundsBox);
    setBoundsSource("where");
  }, [whereBoundsBox, whereFilterId]);

  // Clearing the Where pill (via the soft-X) also clears any active bounds filter,
  // regardless of source. Map-area pills set the value to a synthetic suggestion
  // alongside boundsSource="map", so the same clear gesture wipes both.
  useEffect(() => {
    if (whereValue === null && boundsSource !== null) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setBoundsBox(null);
      setBoundsSource(null);
    }
  }, [whereValue, boundsSource]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleBoundsChange = useCallback(
    (bounds: MapBounds) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const { northEast: ne, southWest: sw } = bounds;
        setBoundsBox(`${clampLat(ne.lat)},${clampLng(ne.lng)},${clampLat(sw.lat)},${clampLng(sw.lng)}`);
        setBoundsSource("map");
        markMapArea(mapAreaLabel);
      }, BOUNDS_DEBOUNCE_MS);
    },
    [markMapArea, mapAreaLabel],
  );

  return { boundsBox, boundsSource, handleBoundsChange };
}
